/**
 * Step 3's questions — before the plans run, and again under "Things that change the answer" (granny flat
 * rebuild, 21 Oct 2026; the agreed mock-up's j1 questions screen, with the village's rulings carried over).
 *
 * Only the levers the client actually has, each asked only where a plan turns on it:
 *   1. has the family talked it through — the family discussion guide, linked (its answers go into a brief
 *      for the solicitor);
 *   2. if they keep the home and rent it out: the rent, its yearly costs and the weeks it sits empty (two,
 *      suggested), with the check on land tax — asked only where keeping the home is tested;
 *   3. the money kept for emergencies (kept back when money moves, drawn on only if a year runs short);
 *   4. whether they would consider a guaranteed income for life — the shared question every module uses,
 *      the client's own answer, "we work out how much" — asked only where a sale frees money to put in one;
 *   5. moving out of a village: when the current unit is paid out;
 *   6. where a sale frees money: the state a bought property's stamp duty is struck in (said, not asked
 *      again, when the file has one) and how money that is invested is assumed to be invested;
 * and the years, as a default on one line. "Build a plan yourself instead" sits beside the run button.
 */
import React, { useMemo, useState } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { STAMP_DUTY_STATES, isStampDutyState } from '../../../../shared/src/calculation/stampDuty.ts';
import { villageInterestHeld, villageExitEntitlement, villageFeesAfterLeaving, todayIsoOf } from '../../../../shared/src/calculation/support/villageExit.ts';
import { villageMoveOutLine } from '../agedCare/villageExitWords.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import { GuaranteedIncomeQuestions, type GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import { annuityPreferenceOf } from '../../../../shared/src/calculation/annuityPreference.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import { DEFAULT_GF_VACANCY_WEEKS, GF_SETUP_SPEND_THRESHOLD, knownPurchaseStateOf } from '../../strategy/grannyFlatRecommendedStrategies.ts';
import { gfGiveOf } from './GfStep1.tsx';
import { QuestionTitle, BuildYourselfLine } from '../../components/strategy/QuestionParts.tsx';

export interface GfAnswers {
    rentPerWeek: number;
    rentCosts: number;
    vacancyWeeks: number;
    emergencyReserve: number;
    /** Whether a guaranteed income may be tested, as the client's answer reads (annuityPreference.ts). */
    annuity: 'yes' | 'none';
    /**
     * The guaranteed-income answers given on this screen (GuaranteedIncomeQuestions), written to the CLIENT's
     * record when the plans run; null when unchanged here. The client's earlier answer is never rewritten
     * with this screen's defaults (guaranteed income review, 21 Oct 2026).
     */
    guaranteedIncome: GuaranteedIncomeGoals | null;
    purchaseState: string;
    oldUnitPaysAtMove: boolean;
    investProfile: string;
    years: number;
}

const num = (x: unknown, d = 0) => (x != null && x !== '' && Number.isFinite(Number(x)) ? Number(x) : d);

/** What a sale leaves to invest after the right is paid for and the reserve topped up (the generator's sum). */
export function gfSaleInvestable(homeValue: number, fair: number, reserve: number, bankToday: number): number {
    const sellCosts = Math.round(homeValue * 0.025);
    const reserveTopUp = Math.max(0, reserve - bankToday);
    return Math.max(0, homeValue - sellCosts - fair - reserveTopUp);
}

export function gfAnswersFromGoals(factFind: DraftFactFind): GfAnswers {
    const g = (factFind?.goals || {}) as Record<string, unknown>;
    const appetite = annuityPreferenceOf(factFind as never).appetite;
    const likelyRent = num(factFind?.home?.likelyRent);
    const rentPerWeek = num(g.gfRentPerWeek, likelyRent > 0 ? likelyRent : Math.round((num(factFind?.home?.value) * 0.035) / 52));
    // A year of costs: the client's, else the profile's, else a quarter of the rent (owner's granny flat review,
    // 24 Oct 2026). It defaulted to $0 when the profile had none, and that $0 was then saved as an answer, so
    // every let home was modelled with no rates, insurance or repairs at all.
    const profileCosts = num(factFind?.home?.likelyExpenses);
    return {
        rentPerWeek,
        rentCosts: num(g.gfRentCosts, profileCosts > 0 ? profileCosts : Math.round(rentPerWeek * 52 * 0.25)),
        vacancyWeeks: num(g.gfVacancyWeeks, DEFAULT_GF_VACANCY_WEEKS),
        emergencyReserve: num(g.gfEmergencyReserve, getRecommendedEmergencyFund(factFind)),
        annuity: appetite === 'none' ? 'none' : 'yes',
        guaranteedIncome: null,
        purchaseState: knownPurchaseStateOf(factFind),
        oldUnitPaysAtMove: g.gfOldUnitPaysAtMove !== false,
        investProfile: String(g.newMoneyInvestmentProfile || ''),
        years: num(g.gfLegacyYears, 10),
    };
}

/**
 * The answers as they are kept on the file. The guaranteed-income answers are the CLIENT's (read by every
 * module): only what was answered on this screen is written, dated.
 */
export function gfAnswersToGoals(a: GfAnswers): Record<string, unknown> {
    return {
        gfRentPerWeek: a.rentPerWeek,
        gfRentCosts: a.rentCosts,
        gfVacancyWeeks: a.vacancyWeeks,
        gfEmergencyReserve: a.emergencyReserve,
        ...(a.guaranteedIncome || {}),
        gfPurchaseState: a.purchaseState,
        gfOldUnitPaysAtMove: a.oldUnitPaysAtMove,
        ...(a.investProfile ? { newMoneyInvestmentProfile: a.investProfile } : {}),
        gfLegacyYears: a.years,
    };
}

interface GfQuestionsProps {
    factFind: DraftFactFind;
    /** Centrelink's value of the right at the start date (what a sale pays before the rest is invested). */
    reasonableValue: number | null;
    /** Whether the questions open before the first run (true) or under "Things that change the answer". */
    firstRun: boolean;
    onRun: (answers: GfAnswers) => void;
    onBuildYourOwn: () => void;
    onFamilyGuide: () => void;
    onCancel?: () => void;
}

const link = 'font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy';

const Why = ({ open, onToggle, children }: { open: boolean; onToggle: () => void; children: React.ReactNode }) => (
    <>
        {' '}<button type="button" onClick={onToggle} aria-expanded={open} className="text-[12px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2">what&apos;s this?</button>
        {open && <span className="mt-1.5 block rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-[13px] font-normal leading-relaxed text-slate-700">{children}</span>}
    </>
);

const Radio = ({ name, checked, onChange, children, testId }: { name: string; checked: boolean; onChange: () => void; children: React.ReactNode; testId?: string }) => (
    <label className="flex cursor-pointer items-start gap-2 py-1 text-[14px] text-slate-700">
        <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-1 accent-blue-600" data-testid={testId} />
        <span>{children}</span>
    </label>
);

/** One question; defined outside the screen so its inputs keep focus. */
/** Numbered as every module's questions are (owner, 23 Oct 2026: the granny flat's were the only ones without). */
const Q = ({ n, title, why, whyOpen = false, onWhy, children, testId }: { n: number; title: React.ReactNode; why?: React.ReactNode; whyOpen?: boolean; onWhy?: () => void; children: React.ReactNode; testId?: string }) => (
    <div className="border-t border-slate-200 pt-4" data-testid={testId}>
        <QuestionTitle n={n}>{title}{why && onWhy && <Why open={whyOpen} onToggle={onWhy}>{why}</Why>}</QuestionTitle>
        <div className="mt-2">{children}</div>
    </div>
);

const GfQuestions = ({ factFind, reasonableValue, firstRun, onRun, onBuildYourOwn, onFamilyGuide, onCancel }: GfQuestionsProps) => {
    const v = householdVoice(factFind);
    const [a, setA] = useState<GfAnswers>(() => gfAnswersFromGoals(factFind));
    const set = (patch: Partial<GfAnswers>) => setA((prev) => ({ ...prev, ...patch }));
    const [openWhy, setOpenWhy] = useState<string | null>(null);
    const whyProps = (id: string) => ({ whyOpen: openWhy === id, onWhy: () => setOpenWhy((o) => (o === id ? null : id)) });
    const [editState, setEditState] = useState(false);
    const goals = (factFind?.goals || {}) as Record<string, unknown>;
    const homeValue = num(factFind?.home?.value);
    const hasHome = homeValue > 0;
    const give = gfGiveOf(factFind);
    const route = give === 'home' ? 'transfer' : goals.gfHomeRoute === 'keep' || goals.gfHomeRoute === 'sell' ? String(goals.gfHomeRoute) : 'all';
    const keeps = hasHome && (route === 'keep' || route === 'all');
    const sells = hasHome && (route === 'sell' || route === 'all');
    const suggestedReserve = getRecommendedEmergencyFund(factFind);
    const bankToday = (factFind?.assets?.bankAccounts || []).reduce((t, x) => t + num(x?.value), 0);
    const fair = Math.round(reasonableValue ?? 0);
    // What a sale pays for the right: the formula value, or the build / cash step 1 described where that is
    // smaller (24 Oct 2026: the engine now tests that form with a sale too, so either can leave money to invest).
    const goalsNow = (factFind?.goals || {}) as { gfGive?: string; gfKnownAmount?: number; gfKnownExtraCash?: number };
    const known = (goalsNow.gfGive === 'build' || goalsNow.gfGive === 'cash') && num(goalsNow.gfKnownAmount) > 0
        ? num(goalsNow.gfKnownAmount) + (goalsNow.gfGive === 'build' ? num(goalsNow.gfKnownExtraCash) : 0)
        : 0;
    const investable = gfSaleInvestable(homeValue, known > 0 ? Math.min(fair, known) : fair, a.emergencyReserve, bankToday);
    const spends = sells && investable >= GF_SETUP_SPEND_THRESHOLD;
    const stateOnFile = isStampDutyState(knownPurchaseStateOf(factFind)) ? STAMP_DUTY_STATES.find((s) => s.code === knownPurchaseStateOf(factFind)) : undefined;
    const chosenState = isStampDutyState(a.purchaseState) ? STAMP_DUTY_STATES.find((s) => s.code === a.purchaseState) : undefined;
    const currentVillage = useMemo(() => {
        const ff = factFind as unknown as FactFind;
        if (!villageInterestHeld(ff)) return null;
        const today = todayIsoOf(new Date());
        const e = villageExitEntitlement(ff, today);
        return e ? { name: ff.client?.firstName || 'The client', ...e, feesAfterLeaving: villageFeesAfterLeaving(ff.retirementVillage, today, e.refundDate) } : null;
    }, [factFind]);
    // The client's guaranteed-income answers as this screen shows them: the file's, with any change made here.
    const giView = useMemo(() => (a.guaranteedIncome
        ? ({ ...factFind, goals: { ...((factFind?.goals || {}) as Record<string, unknown>), ...a.guaranteedIncome } } as DraftFactFind)
        : factFind), [factFind, a.guaranteedIncome]);
    // A sale that leaves money to put to work asks two questions: the guaranteed income and how the rest is invested.
    const count = 2 + (keeps ? 1 : 0) + (spends ? 2 : 0) + (currentVillage ? 1 : 0);
    let asked = 0;
    const next = () => { asked += 1; return asked; };
    const countWord = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][count] || String(count);

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="gf-questions">
            <h2 className="text-[22px] font-bold text-brand-navy">{countWord} questions before we test the ways to set it up</h2>
            <p className="mt-1 text-[15px] text-slate-600">Everything else comes from {v.possessive} file and step 1. Our suggestions are filled in, and each can be changed later.</p>
            <BuildYourselfLine onClick={onBuildYourOwn} testId="gf-build-own-top" />

            <div className="mt-4 space-y-4">
                <Q n={next()} title="Has the family talked it through?" testId="gf-q-talked">
                    <p className="text-[14px] leading-relaxed text-slate-700">The arrangement only works if everyone understands it. <button type="button" onClick={onFamilyGuide} className={link} data-testid="gf-questions-guide">Open the family discussion guide</button> — the answers go into a brief for the solicitor.</p>
                </Q>
                {currentVillage && (
                    <Q n={next()} title="When will the current village unit be paid out?" testId="gf-from-village-q">
                        <Radio name="gf-payout" checked={a.oldUnitPaysAtMove} onChange={() => set({ oldUnitPaysAtMove: true })}>At the move</Radio>
                        <Radio name="gf-payout" checked={!a.oldUnitPaysAtMove} onChange={() => set({ oldUnitPaysAtMove: false })}>Later — up to {currentVillage.refundMonths} months after leaving</Radio>
                        <p className="mt-1 text-[13px] text-slate-600" data-testid="gf-from-village">{villageMoveOutLine(currentVillage, 'granny_flat', { atMove: a.oldUnitPaysAtMove })}</p>
                    </Q>
                )}
                {keeps && (
                    <Q n={next()} title={`If ${v.they} keep the home and rent it out, what rent?`} testId="gf-q-rent">
                        {/* The app's own fields at the app's own size (owner, 25 Oct 2026: these looked unlike every other
                            field — the row's small text was shrinking the boxes, and the weeks box had its own style). */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <CurrencyInput value={a.rentPerWeek} onChange={(x) => set({ rentPerWeek: Math.max(0, x ?? 0) })} className="w-36" ariaLabel="Rent a week" />
                            <span className="text-[13px] text-slate-600">a week, less</span>
                            <CurrencyInput value={a.rentCosts} onChange={(x) => set({ rentCosts: Math.max(0, x ?? 0) })} className="w-36" ariaLabel="Yearly costs of the rented home" />
                            <span className="text-[13px] text-slate-600">a year of costs, empty for</span>
                            <input type="number" min={0} max={52} value={a.vacancyWeeks} onChange={(e) => set({ vacancyWeeks: Math.min(52, Math.max(0, parseFloat(e.target.value) || 0)) })} className="w-20 rounded-lg border border-gray-300 bg-white/80 px-3 py-2 transition focus:border-blue-500 focus:ring-blue-500" aria-label="Weeks a year without a tenant" data-testid="gf-vacancy-weeks" />
                            <span className="text-[13px] text-slate-600">weeks a year{a.vacancyWeeks === DEFAULT_GF_VACANCY_WEEKS ? ', suggested' : ''}</span>
                        </div>
                        <p className="mt-2 text-[13.5px] leading-relaxed text-slate-700"><strong className="text-slate-900">Check the costs.</strong> Include council rates, insurance, repairs, the agent&apos;s fee and <strong className="text-slate-900">land tax</strong>, which can apply once the home is no longer where {v.they} live and is often the largest. And <strong className="text-slate-900">allow for empty weeks</strong> between tenants: each one is {formatCurrency(Math.round(a.rentPerWeek))} of rent lost while the costs go on.</p>
                    </Q>
                )}
                <Q n={next()} title="How much should stay in the bank for emergencies?" {...whyProps('reserve')} why={<>Money kept in the bank for the unexpected. We keep it back when we move {v.their} money, and draw on it only if a year runs short. Centrelink still counts it.</>} testId="gf-q-reserve">
                    <div className="flex flex-wrap items-center gap-3">
                        <CurrencyInput value={a.emergencyReserve} onChange={(x) => set({ emergencyReserve: Math.max(0, x ?? 0) })} className="w-36" ariaLabel="Money kept for emergencies" />
                        <span className="text-[13px] text-slate-500">
                            We suggest {formatCurrency(suggestedReserve)}: the greater of six months&apos; living costs and $50,000.
                            {Math.round(a.emergencyReserve) !== Math.round(suggestedReserve) && <> <button type="button" onClick={() => set({ emergencyReserve: suggestedReserve })} className={link}>Use {formatCurrency(suggestedReserve)}</button></>}
                        </span>
                    </div>
                </Q>
                {spends && (
                    <Q n={next()} title="A guaranteed income for life" testId="gf-q-annuity">
                        <GuaranteedIncomeQuestions
                            fromHomeSale
                            factFind={giView}
                            onGoals={(patch) => set({
                                guaranteedIncome: { ...(a.guaranteedIncome || {}), ...patch },
                                ...(patch.annuityAppetite ? { annuity: patch.annuityAppetite === 'none' ? 'none' as const : 'yes' as const } : {}),
                            })}
                            investable={investable}
                            investableLead={`If the home is sold, the right is paid for and ${formatCurrency(Math.round(a.emergencyReserve))} is kept for emergencies`}
                            investableMadeOf="money left from the sale, after the selling costs and the money kept for emergencies"
                            inCare={guaranteedShapeAvailable(factFind as never)}
                            idPrefix="gf"
                        />
                        {a.annuity === 'yes' && <p className="mt-2 text-[13.5px] leading-relaxed text-slate-700"><strong className="text-slate-900">We work out how much.</strong> We test amounts up to that share, so most of {v.their} money stays within reach. Where it would lift the Age Pension, we also test the amount that lifts it most, held to the same share.</p>}
                    </Q>
                )}
                {spends && (
                    <Q n={next()} title="If the home is sold, how is the money left invested?" testId="gf-q-invest">
                        <p className="text-[13.5px] text-slate-600">The plans test investing it, an investment property, super, an investment bond and the bank, and say what each adds.</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-slate-600">
                            <span>Money that is invested is assumed to go into</span>
                            <select value={a.investProfile} onChange={(e) => set({ investProfile: e.target.value })} className="rounded border border-slate-300 px-2 py-1 text-[13px]" aria-label="How money that is freed up is invested">
                                <option value="">Not sure — assume the most cautious</option>
                                {INVESTMENT_PROFILE_IDS.map((id) => <option key={id} value={id}>{INVESTMENT_PROFILES[id].label}</option>)}
                            </select>
                        </div>
                        {stateOnFile && !editState ? (
                            <p className="mt-2 text-[13px] text-slate-600" data-testid="gf-state-on-file">The state for stamp duty on a bought property is already on the file: <strong className="text-slate-900">{stateOnFile.name}</strong>. <button type="button" onClick={() => setEditState(true)} className={link}>Change</button></p>
                        ) : (
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-slate-600">
                                <span>A bought property would be in</span>
                                <select value={chosenState ? chosenState.code : ''} onChange={(e) => set({ purchaseState: e.target.value })} className="rounded border border-slate-300 px-2 py-1 text-[13px]" aria-label="The state a bought property would be in" data-testid="gf-purchase-state">
                                    <option value="">choose the state</option>
                                    {STAMP_DUTY_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                                </select>
                                <span>{chosenState ? 'investor stamp duty is struck on its scale, and the property is sized to what is left.' : 'with no state, no duty is charged, and the property’s card says so.'}</span>
                            </div>
                        )}
                    </Q>
                )}
            </div>

            <p className="mt-5 text-[13px] text-slate-600">
                <ProjectionYearsInput years={a.years} onChange={(y) => set({ years: Math.min(30, Math.max(1, y)) })} before="We plan for" after=" — the same span for every way tested." ariaLabel="Years to plan for" />
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <span className="text-[14px] text-slate-700">Prefer to set the numbers yourself? <button type="button" onClick={onBuildYourOwn} className={link} data-testid="gf-build-own">Build a plan yourself instead</button></span>
                <span className="flex items-center gap-3">
                    {!firstRun && onCancel && <button type="button" onClick={onCancel} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800">Cancel</button>}
                    <button type="button" onClick={() => onRun(a)} className="rounded-lg bg-blue-600 hover:bg-blue-700 px-5 py-2.5 text-[15px] font-semibold text-white " data-testid="gf-run">{firstRun ? 'Test the ways to set it up' : 'Test the ways again'}</button>
                </span>
            </div>
        </div>
    );
};

export default GfQuestions;
